import {Button, ButtonStyle} from '@mintdata-wsp/common/components/button/Button';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {AnySheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {SheetTabNameValidator} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SpreadsheetTabNameValidator';
import {WSPIcon} from '@mintdata-wsp/resources/icons/WSPIcon';
import {WSPIcons, WSPIconSize} from '@mintdata-wsp/resources/icons/WSPIcons';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {SpreadsheetNameEditor} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/tabsPanel/SpreadsheetNameEditor';
import {TabsUtil} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/tabsPanel/TabsUtil';
import {SheetId, WorkbookId} from '@mintdata/mint-kit/api/models/Identifiers';
import {DataSource} from '@mintdata/mint-kit/api/models/List';
import {
    ContextMenu,
    ContextMenuConstructor,
    IContextMenuItem,
    ToggleOn
} from '@mintdata/mint-kit/impl/components/contextMenu/ContextMenu';
import {PopupAnchor, SourceAnchor} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import {IStyles, Styles} from '@mintdata/mint-kit/impl/util/Styles';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {observer} from 'mobx-react';
import * as React from 'react';
import {ActiveUserDots} from '@mintdata-wsp/wsp/ui/views/visualDesigner/common/ActiveUserDots';

const tabStyles = require('@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/tabsPanel/SpreadsheetTab.less');
const cx = classnames.bind(tabStyles);

export type OnRefHandler = (element:HTMLDivElement) => void;
export type TabActivationHandler = (workbookId:WorkbookId, sheetId:SheetId) => void;
export type TabDeletionHandler = (workbookId:WorkbookId, sheetId:SheetId) => void;

enum Actions
{
    ShowEditor = 'showEditor',
    ShowDeletionMessage = 'showDeletionMessage',
    DuplicateTab = 'duplicateTab',
    MoveTabRight = 'moveTabRight',
    MoveTabLeft = 'moveTabLeft',
}

export interface ITabProps
{
    workbookId:WorkbookId;
    isSelected:boolean;
    sheet:AnySheet;
    theme?:IStyles;
    onEditorRef?:OnRefHandler;
    onActivateTab?:TabActivationHandler;
    onDeleteTab?:TabDeletionHandler;
}

export interface ITabState
{
    styles:IStyles
}

enum TabMovingDirection
{
    Left = 'left',
    Right = 'right'
}

@observer
export class SpreadsheetTab extends React.Component<ITabProps, ITabState>
{

    public defaultProps:Partial<ITabProps> = {
        onEditorRef: (element:HTMLDivElement) => null,
        onActivateTab: (workbookId:WorkbookId, sheetId:SheetId) => null
    };

    @bind
    private _actionHandler(action:Actions):void
    {
        const {workbookId, sheet, onDeleteTab} = this.props;
        const wc = controllers.ui.spreadsheets.workbook;
        const tabsController = controllers.ui.spreadsheets.tabs;

        switch( action )
        {
            case Actions.ShowEditor:
                tabsController.setEditorSheetNameId(workbookId, sheet.id);
                tabsController.setEditorSheetName(workbookId, sheet.name);
                break;
            case Actions.DuplicateTab:
                this.props.onActivateTab(workbookId, wc.duplicateAndInsertSheet(workbookId, sheet.id));
                break;
            case Actions.ShowDeletionMessage:
            {
                if( onDeleteTab )
                    onDeleteTab(workbookId, sheet.id);

                break;
            }
            case Actions.MoveTabRight:
                this._moveTab(workbookId, sheet.id, TabMovingDirection.Right);
                break;
            case Actions.MoveTabLeft:
                this._moveTab(workbookId, sheet.id, TabMovingDirection.Left);
                break;
            default:
                return;
        }
    }

    @bind
    private _moveTab(workbookId:WorkbookId, sheetId:SheetId, direction:TabMovingDirection):void
    {
        const {workbook} = controllers.ui.spreadsheets;
        const sheetIndex = workbook.findSheetIndex(workbookId, sheetId);

        const newIndex = (direction === TabMovingDirection.Left)
            ? sheetIndex - 1
            : sheetIndex + 1;

        workbook.moveSheet(workbookId, sheetId, newIndex);

        this._setFocusOnActiveSheet(workbookId);
    }

    @bind
    private _onEditorRef(element:HTMLDivElement):void
    {
        if( !element )
            return;

        this.props.onEditorRef(element);
    }

    @bind
    private _onSpreadsheetNameChange(name:string):void
    {
        const {workbookId} = this.props;
        const tabsController = controllers.ui.spreadsheets.tabs;
        tabsController.setEditorSheetName(workbookId, name);
    }

    @bind
    private _validateName(name:string):boolean
    {
        const {workbookId, sheet} = this.props;
        return SheetTabNameValidator.isValidSheetName(workbookId, sheet.id, name);
    }

    @bind
    private _onSpreadsheetRenameFinish():void
    {
        const {workbookId, sheet} = this.props;

        if( !this._isInEditMode() )
            return;

        const tabsController = controllers.ui.spreadsheets.tabs;
        const {isEditorSheetNameValid, editorSheetName} = tabsController.getTabState(workbookId);

        tabsController.enableEditorErrors(workbookId);
        if( isEditorSheetNameValid )
        {
            this._hideSpreadsheetNameEditor();
            controllers.ui.spreadsheets.workbook.renameSheet(workbookId, sheet.id, editorSheetName.trim());

            this._setFocusOnActiveSheet(workbookId);
        }
    }

    @bind
    private _setFocusOnActiveSheet(workbookId:WorkbookId)
    {
        const {tabs} = controllers.ui.spreadsheets;

        const activeSheet = tabs.getActiveSheet(workbookId);
        if( activeSheet )
        {
            this.props.onActivateTab(workbookId, activeSheet.id);
        }
    }

    @bind
    private _hideSpreadsheetNameEditor():void
    {
        const {workbookId} = this.props;
        const tabsController = controllers.ui.spreadsheets.tabs;
        tabsController.setEditorSheetName(workbookId, null);
        tabsController.setEditorSheetNameId(workbookId, null);
        tabsController.disableEditorErrors(workbookId);
    }

    @bind
    private _isInEditMode()
    {
        const {id} = this.props.sheet;
        const {workbookId} = this.props;
        const tabState = controllers.ui.spreadsheets.tabs.getTabState(workbookId);
        const {editorSheetNameId} = tabState;
        return editorSheetNameId === id;
    }

    public componentWillMount():void
    {
        const {theme} = this.props;

        this._initStyles(theme);
    }

    public componentWillReceiveProps(newProps:ITabProps):void
    {
        const {theme} = this.props;

        if( theme !== newProps.theme )
            this._initStyles(newProps.theme);
    }

    private _initStyles(theme:IStyles):void
    {
        this.setState({
            styles: Styles.mergeStyles(tabStyles, theme || {})
        });
    }

    @bind
    private _tabActionsMenuProvider(target:HTMLElement):DataSource<IContextMenuItem>
    {
        const {workbookId, sheet} = this.props;

        const wc = controllers.ui.spreadsheets.workbook;
        const numSheets = wc.getNumSheets(workbookId);
        const sheetIndex = wc.findSheetIndex(workbookId, sheet.id);

        const isFirstTab = sheetIndex === 0;
        const isLastTab = (sheetIndex === numSheets - 1);
        const isSingleTab = numSheets === 1;

        return [
            {
                label: 'Delete',
                isDisabled: isSingleTab,
                handler: () => this._actionHandler(Actions.ShowDeletionMessage)
            },
            {
                label: 'Duplicate',
                handler: () => this._actionHandler(Actions.DuplicateTab)
            },
            {
                label: 'Rename...',
                handler: () => this._actionHandler(Actions.ShowEditor)
            },
            {
                label: '',
                isSeparator: true
            },
            {
                label: 'Move right',
                isDisabled: isLastTab,
                handler: () => this._actionHandler(Actions.MoveTabRight)
            },
            {
                label: 'Move left',
                isDisabled: isFirstTab,
                handler: () => this._actionHandler(Actions.MoveTabLeft)
            }
        ];
    };

    public render():JSX.Element
    {
        const theme = this.props.theme;
        const {styles} = this.state;
        const cx = classnames.bind(styles);
        const {isSelected, sheet} = this.props;
        const TypedContextMenu:ContextMenuConstructor<any> = ContextMenu as any;
        const sheetTabId = 'sheetTabId';

        const button = <Button
            theme={theme}
            className={cx('buttonMore')}
            style={ButtonStyle.SimpleSecondary}
            icon={MDIcons.common.ArrowDropdown}
        />;

        if( !this._isInEditMode() )
        {
            return (
                <TypedContextMenu
                    togglersIds={[sheetTabId]}
                    toggleOn={[ToggleOn.CONTEXT_MENU]}
                    popupAnchor={PopupAnchor.LeftBottom}
                    sourceAnchor={SourceAnchor.LeftTop}
                    dataSource={this._tabActionsMenuProvider}
                >
                    <div
                        id={sheetTabId}
                        className={cx('tab', {activeTab: isSelected})}
                    >
                        <div className={cx('tabAndButton')} id={TabsUtil.getMenuButtonId(sheet.id)}>
                            {
                                sheet.isCloud &&
                                <WSPIcon className={cx('cloudIcon')}
                                         name={WSPIcons.visualDesigner.spreadsheet.Cloud}
                                         size={WSPIconSize.medium16} />
                            }
                            <div
                                onDoubleClick={() => this._actionHandler(Actions.ShowEditor)}
                                className={cx('previewLabel')}
                            >{sheet.name}</div>
                            <div className={'menuIcon'}>
                                <TypedContextMenu
                                    toggleOn={[ToggleOn.CLICK]}
                                    togglersIds={[sheetTabId]}
                                    popupAnchor={PopupAnchor.LeftBottom}
                                    sourceAnchor={SourceAnchor.LeftTop}
                                    dataSource={this._tabActionsMenuProvider}
                                >
                                    {button}
                                </TypedContextMenu>
                            </div>
                        </div>
                        <ActiveUserDots filterSheetId={sheet.id} />
                    </div>
                </TypedContextMenu>
            );
        }
        else
        {
            return (
                <div className={cx('tab', {activeTab: isSelected})}>
                    <div className={cx('tabAndButton')} id={TabsUtil.getMenuButtonId(sheet.id)}>
                        {
                            sheet.isCloud &&
                            <WSPIcon className={cx('cloudIcon')}
                                     name={WSPIcons.visualDesigner.topBar.Cloud}
                                     size={WSPIconSize.medium16} />
                        }
                        <SpreadsheetNameEditor
                            initialValue={sheet.name}
                            validateFn={this._validateName}
                            onRef={this._onEditorRef}
                            onTextChanged={this._onSpreadsheetNameChange}
                            onComplete={this._onSpreadsheetRenameFinish}
                            onCancel={this._hideSpreadsheetNameEditor}
                        />
                        {button}
                    </div>
                </div>
            );
        }
    }
}
